/**
 * echoRegister.js — совмещение кадров эхокардиографии по сдвигу.
 *
 * ЗАЧЕМ. Датчик в руке дрожит, пациент дышит, и один и тот же срез в разных
 * кадрах смещён на несколько миллиметров. Пока кадры не совмещены, любая
 * попытка сравнить их числом — измерить утолщение стенки, сложить кадры для
 * снижения шума, посмотреть кривую яркости в точке — считает не изменение
 * ткани, а движение датчика.
 *
 * ЧТО ЗДЕСЬ ЕСТЬ. Только ЖЁСТКИЙ СДВИГ по двум осям, найденный по нормированной
 * взаимной корреляции, с уточнением до долей пикселя параболой по трём точкам
 * вокруг максимума. Нормированная — обязательное слово: обычная корреляция
 * растёт там, где ярче, и уводит совмещение на самое светлое место кадра (у
 * эхо это перикард и рёберная тень), а не туда, где картинки совпадают.
 * Нормировка на средние и разбросы внутри окна убирает и разную общую яркость,
 * и разное усиление.
 *
 * ЧЕГО ЗДЕСЬ НЕТ, и это принципиально:
 *   · НЕТ ПОВОРОТА И МАСШТАБА. Наклон датчика даёт поворот, и жёсткий сдвиг его
 *     не описывает. Программа не молчит об этом: качество совмещения падает, и
 *     оценка `надёжно` становится false — лучше отказ, чем совмещение, которое
 *     похоже на правду в центре и врёт по краям.
 *   · НЕТ УПРУГОЙ ДЕФОРМАЦИИ. Сердце сокращается — стенка меняет форму, и
 *     совместить сокращающийся миокард сдвигом нельзя в принципе. Поэтому
 *     совмещать надо кадры ОДНОЙ фазы (по ЭКГ-метке или по номеру кадра в цикле),
 *     а не подряд идущие. Функция для этого и есть — `совместитьФазу`.
 *   · НЕТ УЧЁТА СЕКТОРА. У эхо кадр — сектор, вне него чёрные углы. Они
 *     одинаковы во всех кадрах и корреляцию завышают: совмещение «по черноте»
 *     выглядит идеальным при любом сдвиге. Поэтому пиксели ниже порога
 *     информативности из подсчёта ВЫКЛЮЧАЮТСЯ, и доля использованных пикселей
 *     возвращается наружу: если она мала, совмещение считалось по горстке точек,
 *     и доверия ему нет.
 *   · Смещение ищется в заданном окне поиска. Сдвиг больше окна не будет найден
 *     вовсе — вернётся ответ на границе окна, и он помечается как ненадёжный.
 *
 * Качество — не украшение. Возвращается сама корреляция в максимуме, её перевес
 * над вторым по величине пиком и доля информативных пикселей. Совмещение
 * объявляется надёжным, только когда все три условия выполнены; иначе кадр
 * остаётся несовмещённым И ГОВОРИТСЯ ПОЧЕМУ. Молча сдвинуть кадр на найденное
 * «что-нибудь» — худший вариант: на картинке это выглядит убедительно.
 */

/** Причины отказа. */
export const ПРИЧИНЫ = Object.freeze({
  РАЗМЕР: 'кадры разного размера',
  ПУСТО: 'информативных пикселей слишком мало (сектор, чернота, потеря сигнала)',
  ПЛОСКО: 'корреляция без выраженного максимума — сдвиг не определён',
  ГРАНИЦА: 'максимум на границе окна поиска — настоящий сдвиг больше окна',
  ОДНОРОДНО: 'в окне нет структуры: разброс яркости почти нулевой',
});

const ПО_УМОЛЧАНИЮ = Object.freeze({
  окноПоиска: 12,        // ± пикселей по каждой оси
  // Порог отбраковки — доля от наибольшей яркости кадра. Задача одна: выкинуть
  // черноту ВНЕ сектора, а не приглушённый спекл внутри него. Замер на фантоме
  // сектора: при 0,08 отбраковывался и фон внутри сектора, информативными
  // оставались 9% пикселей, и совмещение объявлялось ненадёжным на верных
  // кадрах; при 0,03 остаётся 45% — весь сектор, как и задумано.
  порогИнформативности: 0.03,
  минДоляПикселей: 0.10, // меньше — считали по горстке точек
  минКорреляция: 0.5,    // ниже — картинки не совпадают ни при каком сдвиге
  минПеревес: 1.06,      // максимум обязан превзойти второй пик хотя бы на 6%
  шаг: 1,                // прореживание при подсчёте (2 — вдвое быстрее)
});

/**
 * Нормированная взаимная корреляция двух кадров при заданном сдвиге.
 *
 * Считается ТОЛЬКО по информативным пикселям (оба выше порога): чёрные углы
 * сектора совпадают при любом сдвиге и завышали бы ответ.
 *
 * @returns {{нкк: number, пикселей: number}}
 */
export function корреляцияПриСдвиге(a, b, ширина, высота, dx, dy, порогЯркости, шаг = 1) {
  let n = 0, сумA = 0, сумB = 0, сумAA = 0, сумBB = 0, сумAB = 0;
  const x0 = Math.max(0, -dx), x1 = Math.min(ширина, ширина - dx);
  const y0 = Math.max(0, -dy), y1 = Math.min(высота, высота - dy);
  for (let y = y0; y < y1; y += шаг) {
    for (let x = x0; x < x1; x += шаг) {
      const va = a[y * ширина + x];
      const vb = b[(y + dy) * ширина + (x + dx)];
      if (!(va > порогЯркости) || !(vb > порогЯркости)) continue;
      n++; сумA += va; сумB += vb;
      сумAA += va * va; сумBB += vb * vb; сумAB += va * vb;
    }
  }
  if (n < 16) return { нкк: -1, пикселей: n };
  const срA = сумA / n, срB = сумB / n;
  const дA = сумAA / n - срA * срA, дB = сумBB / n - срB * срB;
  if (!(дA > 1e-12) || !(дB > 1e-12)) return { нкк: -1, пикселей: n };
  const ков = сумAB / n - срA * срB;
  return { нкк: ков / Math.sqrt(дA * дB), пикселей: n };
}

/** Уточнение максимума до долей пикселя: парабола по трём точкам. */
function подпиксель(лево, центр, право) {
  const знам = лево - 2 * центр + право;
  if (!(Math.abs(знам) > 1e-12)) return 0;
  const d = 0.5 * (лево - право) / знам;
  return Math.abs(d) <= 1 ? d : 0;
}

/**
 * Найти сдвиг кадра `кадр` относительно `опора`.
 *
 * @param {{данные: ArrayLike<number>, ширина: number, высота: number}} опора
 * @param {{данные: ArrayLike<number>, ширина: number, высота: number}} кадр
 * @param {object} [нстр]
 * @returns {{ок: boolean, причина?: string, dx?: number, dy?: number, нкк?: number,
 *            перевес?: number, доляПикселей?: number, надёжно?: boolean, наГранице?: boolean}}
 */
export function найтиСдвиг(опора, кадр, нстр = {}) {
  const н = { ...ПО_УМОЛЧАНИЮ, ...нстр };
  const ш = опора?.ширина, в = опора?.высота;
  if (!опора?.данные?.length || !кадр?.данные?.length) return { ок: false, причина: 'кадр без данных' };
  if (кадр.ширина !== ш || кадр.высота !== в || кадр.данные.length !== опора.данные.length) {
    return { ок: false, причина: ПРИЧИНЫ.РАЗМЕР };
  }

  let макс = 0;
  for (let i = 0; i < опора.данные.length; i++) if (опора.данные[i] > макс) макс = опора.данные[i];
  if (!(макс > 0)) return { ок: false, причина: ПРИЧИНЫ.ПУСТО };
  const порогЯркости = макс * н.порогИнформативности;

  const R = Math.max(1, н.окноПоиска | 0);
  const сторона = 2 * R + 1;
  const поле = new Float64Array(сторона * сторона).fill(-1);
  const пикс = new Int32Array(сторона * сторона);
  let лучшая = -2, лучшийDX = 0, лучшийDY = 0, лучшихПикс = 0;

  for (let dy = -R; dy <= R; dy++) {
    for (let dx = -R; dx <= R; dx++) {
      const { нкк, пикселей } = корреляцияПриСдвиге(опора.данные, кадр.данные, ш, в, dx, dy, порогЯркости, н.шаг);
      поле[(dy + R) * сторона + (dx + R)] = нкк;
      пикс[(dy + R) * сторона + (dx + R)] = пикселей;
      if (нкк > лучшая) { лучшая = нкк; лучшийDX = dx; лучшийDY = dy; лучшихПикс = пикселей; }
    }
  }

  if (лучшая <= -1) return { ок: false, причина: ПРИЧИНЫ.ОДНОРОДНО };

  const всегоВозможных = Math.ceil(ш / н.шаг) * Math.ceil(в / н.шаг);
  const доляПикселей = лучшихПикс / всегоВозможных;

  // Перевес над вторым пиком: ищем наибольшее значение ВНЕ окрестности ±2 от
  // найденного максимума. Соседние узлы — это тот же пик, а не второй.
  let второй = -1;
  for (let dy = -R; dy <= R; dy++) {
    for (let dx = -R; dx <= R; dx++) {
      if (Math.abs(dx - лучшийDX) <= 2 && Math.abs(dy - лучшийDY) <= 2) continue;
      const v = поле[(dy + R) * сторона + (dx + R)];
      if (v > второй) второй = v;
    }
  }
  const перевес = второй > 0 ? лучшая / второй : Infinity;
  const наГранице = Math.abs(лучшийDX) === R || Math.abs(лучшийDY) === R;

  // Подпиксельное уточнение — только если максимум не на границе окна.
  let sdx = лучшийDX, sdy = лучшийDY;
  if (!наГранице) {
    const и = (dx, dy) => поле[(dy + R) * сторона + (dx + R)];
    sdx += подпиксель(и(лучшийDX - 1, лучшийDY), лучшая, и(лучшийDX + 1, лучшийDY));
    sdy += подпиксель(и(лучшийDX, лучшийDY - 1), лучшая, и(лучшийDX, лучшийDY + 1));
  }

  const беды = [];
  if (доляПикселей < н.минДоляПикселей) беды.push(ПРИЧИНЫ.ПУСТО);
  if (лучшая < н.минКорреляция) беды.push(ПРИЧИНЫ.ПЛОСКО);
  if (перевес < н.минПеревес) беды.push(ПРИЧИНЫ.ПЛОСКО);
  if (наГранице) беды.push(ПРИЧИНЫ.ГРАНИЦА);

  return {
    ок: true,
    dx: sdx, dy: sdy, нкк: лучшая, перевес, доляПикселей, наГранице,
    надёжно: беды.length === 0,
    причина: беды.length ? беды.join('; ') : undefined,
  };
}

/**
 * Применить сдвиг к кадру (билинейно). Пиксели, для которых источника нет,
 * получают NaN, а не ноль: ноль на эхо — это чёрный сектор, и подделывать его
 * значит потом считать по нему статистику.
 */
export function сдвинуть(кадр, dx, dy) {
  const { данные, ширина: ш, высота: в } = кадр;
  const из = new Float32Array(ш * в).fill(NaN);
  for (let y = 0; y < в; y++) {
    for (let x = 0; x < ш; x++) {
      const sx = x + dx, sy = y + dy;
      const x0 = Math.floor(sx), y0 = Math.floor(sy);
      if (x0 < 0 || y0 < 0 || x0 + 1 >= ш || y0 + 1 >= в) continue;
      const fx = sx - x0, fy = sy - y0;
      const a = данные[y0 * ш + x0], b = данные[y0 * ш + x0 + 1];
      const c = данные[(y0 + 1) * ш + x0], d = данные[(y0 + 1) * ш + x0 + 1];
      из[y * ш + x] = a * (1 - fx) * (1 - fy) + b * fx * (1 - fy) + c * (1 - fx) * fy + d * fx * fy;
    }
  }
  return { данные: из, ширина: ш, высота: в };
}

/**
 * Совместить кадры ОДНОЙ фазы сердечного цикла с первым из них.
 *
 * Почему одной фазы: между фазами стенка сокращается, и это не сдвиг — сдвигом
 * такое совмещать нельзя в принципе. Кадры отбираются по метке фазы, которую
 * даёт вызывающий (номер кадра в цикле, ЭКГ-метка), — угадывать её здесь нечем.
 *
 * @param {Array<{данные, ширина, высота, фаза?: number|string}>} кадры
 * @param {{фаза?: number|string}} [нстр]
 * @returns {{ок: boolean, причина: string|null, опора: number, сдвиги: Array<object>,
 *            совмещено: number, ненадёжных: number, средняяНКК: number}}
 */
export function совместитьФазу(кадры, нстр = {}) {
  if (!Array.isArray(кадры) || кадры.length < 2) {
    return { ок: false, причина: 'нужно хотя бы два кадра одной фазы', опора: -1, сдвиги: [], совмещено: 0, ненадёжных: 0, средняяНКК: NaN };
  }
  const фаза = нстр.фаза;
  const выбор = фаза === undefined ? кадры.map((к, i) => i) : кадры.map((к, i) => (к.фаза === фаза ? i : -1)).filter((i) => i >= 0);
  if (выбор.length < 2) {
    return { ок: false, причина: `кадров фазы «${фаза}» меньше двух — совмещать нечего`, опора: -1, сдвиги: [], совмещено: 0, ненадёжных: 0, средняяНКК: NaN };
  }

  const опора = выбор[0];
  const сдвиги = [];
  let сумма = 0, надёжных = 0, ненадёжных = 0;
  for (const i of выбор) {
    if (i === опора) { сдвиги.push({ кадр: i, dx: 0, dy: 0, нкк: 1, надёжно: true, опорный: true }); continue; }
    const и = найтиСдвиг(кадры[опора], кадры[i], нстр);
    if (!и.ок) { сдвиги.push({ кадр: i, ...и }); ненадёжных++; continue; }
    сдвиги.push({ кадр: i, ...и });
    if (и.надёжно) { сумма += и.нкк; надёжных++; } else ненадёжных++;
  }

  return {
    ок: надёжных > 0,
    причина: надёжных > 0 ? null : 'ни один кадр не совместился надёжно — сдвиг найден, но качество ниже порога',
    опора, сдвиги,
    совмещено: надёжных, ненадёжных,
    средняяНКК: надёжных ? сумма / надёжных : NaN,
  };
}

/** Оговорки, обязанные стоять рядом с совмещёнными кадрами. */
export function оговоркиСовмещения(и = {}) {
  const о = [
    'Совмещается только сдвиг. Поворот датчика и упругая деформация стенки не учитываются — по краям кадра расхождение останется.',
    'Совмещать имеет смысл кадры одной фазы цикла: между фазами стенка сокращается, и это не сдвиг.',
    'Чёрные углы сектора из расчёта исключены: по ним корреляция была бы идеальной при любом сдвиге.',
  ];
  if (Number.isFinite(и.средняяНКК) && и.средняяНКК < 0.75) {
    о.unshift(`Средняя корреляция ${и.средняяНКК.toFixed(2)} — это немного: вероятен поворот датчика или смена среза, и совмещению сдвигом здесь доверия мало.`);
  }
  if (и.ненадёжных) о.unshift(`Кадров, не совмещённых надёжно: ${и.ненадёжных}. Они оставлены как есть — сдвигать их «на всякий случай» значит испортить сравнение молча.`);
  return о;
}

export default { найтиСдвиг, сдвинуть, совместитьФазу, корреляцияПриСдвиге, оговоркиСовмещения, ПРИЧИНЫ };
